import { createElement, forwardRef } from 'react'

export const Box = forwardRef(({
  basis, direction, grow, shrink, wrap, alignItems, justifyContent, alignSelf, justifySelf, gap,
  fullWidth, fullHeight, fullScreen, container, style, display, textAlign, sizing, scale, column,
  position, width, height, size, top, right, bottom, left, maxWidth, maxHeight, minWidth, minHeight, inset,
  background, backgroundSize, shadow, zIndex, border, radius, transform, transformOrigin, transition, color,
  overflow, overflowX, overflowY, opacity, filter, onClick, cursor, animation, className, userSelect, aspect, rotate,
  m, mt, mb, mr, ml, mx, my, p, pt, pb, pr, pl, px, py, r, ...props
}, ref) => {
  return createElement(
    'div',
    {
      ref,
      onClick,
      className,
      style: {
        display: 'flex',
        boxSizing: 'border-box',
        gap,
        flexBasis: basis,
        flexDirection: direction || (column !== undefined ? (column ? 'column' : 'row') : undefined),
        flexGrow: grow === true ? 1 : grow,
        flexShrink: shrink,
        ...(wrap !== undefined && { flexWrap: wrap === true ? 'wrap' : wrap }),
        ...(alignItems && { alignItems }),
        ...(alignSelf && { alignSelf }),
        ...(justifyContent && { justifyContent }),
        ...(justifySelf && { justifyContent }),
        ...((m || m === 0) && { margin: m }),
        ...((mt || mt === 0) && { marginTop: mt }),
        ...((mb || mb === 0) && { marginBottom: mb }),
        ...((mr || mr === 0) && { marginRight: mr }),
        ...((ml || ml === 0) && { marginLeft: ml }),
        ...((mx || mx === 0) && { marginLeft: mx, marginRight: mx }),
        ...((my || my === 0) && { marginTop: my, marginBottom: my }),
        ...((p || p === 0) && { padding: p }),
        ...((px || px === 0) && { paddingLeft: px, paddingRight: px }),
        ...((py || py === 0) && { paddingTop: py, paddingBottom: py }),
        ...((pt || pt === 0) && { paddingTop: pt }),
        ...((pb || pb === 0) && { paddingBottom: pb }),
        ...((pr || pr === 0) && { paddingRight: pr }),
        ...((pl || pl === 0) && { paddingLeft: pl }),
        ...(fullScreen && {
          minWidth: '100vw',
          width: '100vw',
          minHeight: '100vh',
          height: '100vh'
        }),
        ...(size !== undefined && { width: size, height: size, minHeight: size, minWidth: size }),
        ...(width !== undefined && { width }),
        ...(height !== undefined && { height }),
        ...(maxWidth !== undefined && { maxWidth }),
        ...(maxHeight !== undefined && { maxHeight }),
        ...(minWidth !== undefined && { minWidth }),
        ...(minHeight !== undefined && { minHeight }),
        ...(top !== undefined && { top }),
        ...(right !== undefined && { right }),
        ...(bottom !== undefined && { bottom }),
        ...(left !== undefined && { left }),
        ...(fullHeight && { height: '100%' }),
        ...(fullWidth && { width: '100%' }),
        ...(inset !== undefined && { inset: inset === true ? 0 : inset }),
        ...(container && { position: 'relative' }),
        ...(zIndex !== undefined && { zIndex }),
        ...(position && { position }),
        ...(overflow && { overflow }),
        ...(overflowX && { overflowX }),
        ...(overflowY && { overflowY }),
        ...(opacity !== undefined && { opacity }),
        ...(display && { display }),
        ...(textAlign && { textAlign }),
        ...(sizing && { boxSizing: sizing }),
        ...(background && { background }),
        ...(backgroundSize && { backgroundSize }),
        ...(border && { border }),
        ...((radius !== undefined || r !== undefined) && { borderRadius: (radius || r) }),
        ...(shadow && { boxShadow: shadow }),
        ...(transform && { transform }),
        ...(transformOrigin && { transformOrigin }),
        ...(transition && { transition: typeof transition === 'number' ? (transition + 's') : transition }),
        ...(color && { color }),
        ...(scale && { transform: `scale(${scale})${transform ? ` ${transform}` : ''}` }),
        ...(filter && { filter }),
        ...(cursor && { cursor }),
        ...(animation && { animation }),
        ...(userSelect && { userSelect }),
        ...(aspect && { aspectRatio: aspect }),
        ...(rotate && { rotate: typeof rotate === 'string' ? rotate : (rotate + 'deg') }),
        ...style
      },
      ...props
    }
  )
})

export const RowBox = forwardRef(({
  basis, grow, shrink, wrap, fullWidth, fullHeight, fullScreen, alignItems, justifyContent, alignSelf, justifySelf, gap,
  zIndex, width, height, size, top, right, bottom, left, maxWidth, maxHeight, minWidth, minHeight, display, textAlign, background,
  shadow, radius, overflow, opacity, onClick, ...props
}, ref) => createElement(
  Box,
  {
    direction: 'row',
    basis, grow, shrink, wrap, fullWidth, fullHeight, fullScreen, alignItems, justifyContent, alignSelf, justifySelf, gap,
    zIndex, width, height, size, top, right, bottom, left, maxWidth, maxHeight, minWidth, minHeight, display, textAlign, background,
    shadow, radius, overflow, opacity, ref, onClick, ...props
  }
))

export const SpacedRow = props => createElement(
  RowBox,
  {
    alignItems: 'center',
    justifyContent: 'space-between',
    fullWidth: true,
    ...props
  }
)

export const ColumnBox = forwardRef(({
  basis, grow, shrink, wrap, fullWidth, fullHeight, fullScreen, alignItems, justifyContent, alignSelf, justifySelf, gap,
  zIndex, width, height, size, top, right, bottom, left, maxWidth, maxHeight, minWidth, minHeight, display, textAlign, background,
  shadow, radius, overflow, opacity, onClick, ...props
}, ref) => createElement(
  Box,
  {
    direction: 'column',
    basis, grow, shrink, wrap, fullWidth, fullHeight, fullScreen, alignItems, justifyContent, alignSelf, justifySelf, gap,
    zIndex, width, height, size, top, right, bottom, left, maxWidth, maxHeight, minWidth, minHeight, display, textAlign, background,
    shadow, radius, overflow, opacity, ref, onClick, ...props
  }
))

export const CenteredBox = forwardRef(({ fullWidth, fullHeight, ...props }, ref) => createElement(
  Box,
  {
    alignItems: 'center',
    justifyContent: 'center',
    fullWidth,
    fullHeight,
    ref,
    ...props
  }
))

export const Screen = forwardRef(({ mobile, alignItems = 'center', justifyContent, className, column = true, ...props }, ref) => createElement(
  Box,
  {
    column,
    ref,
    alignItems,
    justifyContent,
    fullScreen: true,
    ...(mobile && {
      height: '100%',
      minHeight: '100%'
    }),
    overflow: 'hidden',
    className: `screen${className ? ` ${className}` : ''}`,
    ...props
  }
))
